import React, { useEffect, useState } from 'react';
import styles from './service-form.module.scss';
import {
  DefaultButton,
  DescriptionMiniInput,
  MiniInput,
  ModalWindow,
  ServiceEmployeesSelector,
  UniversalImage,
} from 'shared/ui';
import { businessStore } from 'entities/index';
import { observer } from 'mobx-react';
import { validateInputs } from 'shared/lib';
import { CreateServiceErrors } from 'shared/consts/create-service.error';
import { IServiceCategory } from 'shared/api/booking/interfaces/service-categories.interface';
import { IEmployeeInfo } from 'shared/api/business/interfaces/employee-info.interface';
import { EditImageComponent } from 'features/index';
import { IServicesCard } from 'shared/api/booking/interfaces/services-list.interface';

interface IServiceFormData {
  name: string;
  price: number;
  serviceCategoryId?: string;
  duration: number;
  description: string;
  employeeIds: string[];
  imgUrl: string;
}

interface ServiceFormProps {
  initialData?: IServicesCard;
  onClose?: () => void;
  onSubmit: (data: IServiceFormData) => Promise<void>;
  onDelete?: () => Promise<void>;
  onDuplicate?: () => void;
  isDuplicate?: boolean;
}

const durationsArray = Array.from({ length: 60 }, (_, i) => (i + 1) * 5).map(
  (num) => String(num) + ' мин.'
);

const validateForm = (params: {
  name: string;
  price: string;
  duration: string;
  employees: IEmployeeInfo[];
}) => {
  const { name, price, duration, employees } = params;
  if (!validateInputs.validateServiceName(name)) {
    return CreateServiceErrors.INCORRECT_NAME;
  }
  if (!validateInputs.validatePrice(price)) {
    return CreateServiceErrors.INCORRECT_PRICE;
  }
  if (!validateInputs.validateServiceDuration(duration)) {
    return CreateServiceErrors.INCORRECT_DURATION;
  }
  if (!validateInputs.validateServiceEmployees(employees)) {
    return CreateServiceErrors.INCORRECT_EMPLOYEES;
  }
  return false;
};

export const ServiceForm: React.FC<ServiceFormProps> = observer(
  ({ initialData, onClose, onSubmit, onDelete, onDuplicate, isDuplicate }) => {
    const isEdit = !!initialData && !isDuplicate;

    const [name, setName] = useState(initialData?.name ?? '');
    const [price, setPrice] = useState(
      initialData ? String(initialData.price) : ''
    );
    const [imgUrl, setImgUrl] = useState(initialData?.imgUrl ?? '');
    const [category, setCategory] = useState<IServiceCategory>(
      initialData?.category
        ? {
            id: initialData.category.id,
            category: initialData.category.category,
          }
        : { id: '', category: '' }
    );
    const [duration, setDuration] = useState(
      initialData ? String(initialData.duration) : ''
    );
    const [description, setDescription] = useState(
      initialData?.description ?? ''
    );
    const [employees, setEmployees] = useState<IEmployeeInfo[]>(
      initialData?.employees ?? []
    );
    const [validationError, setValidationError] = useState('');
    const [showEditImage, setShowEditImage] = useState(false);
    const [modalComponent, setModalComponent] = useState<React.ReactNode>(
      <></>
    );

    const pointId = businessStore.pointId;

    const clearError = () => {
      setValidationError('');
    };

    useEffect(() => {
      if (pointId) {
        businessStore.initServiceCategories(pointId);
      }
    }, [pointId]);

    const handleSubmit = async (e: React.MouseEvent) => {
      const errors = validateForm({ name, price, duration, employees });
      if (errors) {
        setValidationError(errors);
        e.stopPropagation();
        return;
      }
      try {
        setValidationError('');
        const data: IServiceFormData = {
          name,
          price: Number(price),
          serviceCategoryId: category.id || undefined,
          duration: parseInt(duration.replace(/\D+/g, '')),
          description,
          employeeIds: employees.map((e) => e.id),
          imgUrl,
        };
        await onSubmit(data);
      } catch (error) {
        if (error instanceof Error) {
          setValidationError(error.message);
        }
        e.stopPropagation();
      }
    };

    const handleDelete = async (e: React.MouseEvent) => {
      if (!onDelete) return;
      try {
        await onDelete();
      } catch (error) {
        if (error instanceof Error) {
          setValidationError(error.message);
        }
        e.stopPropagation();
      }
    };

    const ChangeImageHandler = () => {
      setShowEditImage(true);
      setModalComponent(
        <ModalWindow onClickOutside={setShowEditImage}>
          <EditImageComponent setUrl={setImgUrl} />
        </ModalWindow>
      );
    };

    return (
      <div
        className={styles.create_service__container}
        onClick={(e) => {
          if (
            (e.target as HTMLElement).closest(`.${styles.buttons__button}`) ||
            (e.target as HTMLElement).closest(`.${styles.title__close_button}`)
          ) {
            return;
          }
          e.stopPropagation();
        }}
      >
        {showEditImage ? modalComponent : null}

        <div className={styles.container__content}>
          <div className={styles.content__title}>
            <div className={styles.title__text}>
              {isDuplicate
                ? 'Дубликат услуги'
                : isEdit
                  ? 'Редактирование услуги'
                  : 'Создание услуги'}
            </div>
            <button className={styles.title__close_button} onClick={onClose}>
              x
            </button>
          </div>
          <div className={styles.content__head}>
            <div className={styles.head__img}>
              <UniversalImage imgSrc={imgUrl} onClick={ChangeImageHandler} />
            </div>
            <div className={styles.head__inputs}>
              <div className={styles.inputs__input} onClick={clearError}>
                <MiniInput
                  inputValue={name}
                  inputTitle="Название"
                  inputPlaceholder="Название"
                  inputType="text"
                  onChange={setName}
                  className={styles.input_name}
                />
              </div>
              <div className={styles.inputs__input} onClick={clearError}>
                <MiniInput
                  inputValue={price}
                  inputTitle="Стоимость"
                  inputPlaceholder="0"
                  inputType="number"
                  onChange={setPrice}
                  className={styles.input_price}
                />
              </div>
              <div className={styles.inputs__input} onClick={clearError}>
                <MiniInput
                  inputValue={category.category}
                  inputTitle="Категория"
                  inputPlaceholder="Выберите категорию"
                  inputType="text"
                  onChange={setCategory}
                  className={styles.input_category}
                  options={businessStore.serviceCategories}
                />
              </div>
              <div className={styles.inputs__input} onClick={clearError}>
                <MiniInput
                  inputValue={duration}
                  inputTitle="Длительность"
                  inputPlaceholder="0 мин."
                  inputType="text"
                  onChange={setDuration}
                  className={styles.input_duration}
                  options={durationsArray}
                />
              </div>
            </div>
          </div>
          <div className={styles.content__description} onClick={clearError}>
            <DescriptionMiniInput
              inputValue={description}
              inputTitle="Краткое описание услуги"
              inputPlaceholder="Описание...  (до 80 символов)"
              onChange={setDescription}
            />
          </div>
          <div className={styles.content__employees} onClick={clearError}>
            <ServiceEmployeesSelector
              employees={businessStore.employeesWidgetData}
              title="Сотрудники оказывающие услугу"
              onChange={setEmployees}
              activeEmployees={employees.length > 0 ? employees : undefined}
            />
          </div>
          <div className={styles.content__error}>{validationError}</div>
          <div className={styles.content__buttons}>
            {isEdit && onDelete && (
              <div className={styles.buttons__button}>
                <DefaultButton
                  color="red"
                  buttonText="Удалить"
                  className={styles.button_delete}
                  onClick={(e) => {
                    handleDelete(e);
                  }}
                />
              </div>
            )}
            {isEdit && onDuplicate && (
              <div className={styles.buttons__button}>
                <DefaultButton
                  buttonText="Создать дубликат"
                  onClick={onDuplicate}
                />
              </div>
            )}
            {isDuplicate && (
              <div className={styles.buttons__button}>
                <DefaultButton
                  color="red"
                  buttonText="Отмена"
                  onClick={onClose}
                />
              </div>
            )}
            <div className={styles.buttons__button}>
              <DefaultButton
                onClick={(e) => {
                  handleSubmit(e);
                }}
                color="green"
                buttonText={
                  isDuplicate
                    ? 'Создать услугу'
                    : isEdit
                      ? 'Сохранить'
                      : 'Создать услугу'
                }
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
);
